import { createFileRoute } from "@tanstack/react-router"
import {
  BookOpen,
  Calculator,
  CalendarCheck,
  FileText,
  Gauge,
  ReceiptText,
  Scale,
  Truck,
  type LucideIcon,
} from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import { breadcrumbJsonLd, buildHead, collectionPageJsonLd } from "@/core/seo"

import { Breadcrumb, CtaBand } from "../components/landing"
import { HeroBand } from "../components/landing/page-parts"
import { IconTile } from "../components/landing/product-art"
import { FREE_TOOL_LINKS, GUIDE_LINKS, type NavLink } from "../nav-model"

/**
 * Hub for /guides (T827). The list is built from nav-model, so a page added to the Guides menu or
 * the Free tools menu shows up here too. The blog is left out (hidden until it has posts) and so is
 * this page itself.
 */
const GUIDE_CARDS: NavLink[] = GUIDE_LINKS.filter((l) => l.href !== "/guides" && l.href !== "/blog")
const TOOLS: NavLink[] = FREE_TOOL_LINKS

type Tone = Parameters<typeof IconTile>[0]["tone"]

/** Presentation only: one icon per page, by href. */
const ICONS: Record<string, { icon: LucideIcon; tone: Tone }> = {
  "/logbook-method": { icon: Scale, tone: "primary" },
  "/guides/ato-logbook-requirements": { icon: CalendarCheck, tone: "success" },
  "/guides/ato-cents-per-km-rate-history": { icon: Gauge, tone: "warning" },
  "/guides/tax-invoice-requirements": { icon: ReceiptText, tone: "primary" },
  "/rideshare-drivers": { icon: Truck, tone: "success" },
  "/glossary": { icon: BookOpen, tone: "warning" },
  "/cents-per-km-calculator": { icon: Calculator, tone: "primary" },
  "/ato-logbook-template": { icon: CalendarCheck, tone: "success" },
  "/invoice-generator": { icon: FileText, tone: "warning" },
}
const iconFor = (href: string) => ICONS[href] ?? { icon: FileText, tone: "primary" as const }

export const guidesHubSeo = () =>
  buildHead({
    title: "Guides and free tools for sole traders",
    description:
      "ATO logbook rules, cents per kilometre rates, tax invoice requirements and free tools for Australian sole traders, each with its ATO source.",
    path: "/guides",
    jsonLd: [
      breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Guides" }]),
      collectionPageJsonLd({
        name: "Guides and free tools for sole traders",
        path: "/guides",
        items: [...GUIDE_CARDS, ...TOOLS].map((l) => ({ name: l.label, path: l.href })),
      }),
    ],
  })

export const Route = createFileRoute("/_brand-layout/guides")({
  head: () => {
    const { meta, links, scripts } = guidesHubSeo()
    return { meta, links, scripts }
  },
  component: GuidesHubPage,
})

const Section = ({ id, title, links }: { id: string; title: string; links: NavLink[] }) => (
  <section aria-labelledby={id} className="px-band grid gap-4 py-8">
    <h2 id={id} className="text-2xl font-semibold">
      {title}
    </h2>
    <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {links.map(({ label, href, description }) => {
        const { icon, tone } = iconFor(href)
        return (
          <li
            key={href}
            className="border-border bg-card hover:bg-muted/40 has-[a:focus-visible]:ring-ring relative flex items-start gap-3 rounded-xl border p-4 has-[a:focus-visible]:ring-2 forced-colors:has-[a:focus-visible]:outline-2"
          >
            <IconTile icon={icon} tone={tone} />
            <div className="grid gap-1">
              <a
                href={href}
                className="font-semibold outline-none after:absolute after:inset-0 after:content-['']"
              >
                {label}
              </a>
              {description && <p className="text-muted-foreground text-sm">{description}</p>}
            </div>
          </li>
        )
      })}
    </ul>
  </section>
)

export function GuidesHubPage() {
  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Guides" }]} />
      <HeroBand>
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">
          Guides and free tools for sole traders
        </h1>
        <p className="text-muted-foreground max-w-[60ch] text-lg">
          Plain-English guides on logbooks, cents per kilometre and tax invoices, and free tools you
          can use without an account. Each one links to the ATO page it comes from.
        </p>
      </HeroBand>
      <Section id="guides-list" title="Guides" links={GUIDE_CARDS} />
      <Section id="tools-list" title="Free tools" links={TOOLS} />
      <CtaBand
        title="Put the guides to work"
        description={`${TRIAL_DAYS}-day free trial. No card needed.`}
        ctaLabel="Start free trial"
        ctaHref="/login"
      />
    </>
  )
}
